import { APP_PREFIX_PATH } from 'configs/AppConfig'
import * as routeKeys from 'constants/RoutesKeys'

export const PAGES_PREFIX = `${APP_PREFIX_PATH}/pages`
export const ADMINS_PREFIX = `${APP_PREFIX_PATH}/admins`

/** URL segments keyed by permission key (RoutesKeys). */
export const PAGE_ROUTE_SEGMENTS = {
  [routeKeys.templateKey]: {
    slug: 'template',
    list: 'template-list',
    add: 'add-template',
    edit: 'edit-template',
  },
  [routeKeys.widgetsKey]: {
    slug: 'widgets',
    list: 'widgets-list',
    add: 'add-widget',
    edit: 'edit-widget',
  },
  [routeKeys.bannerKey]: {
    slug: 'banners',
    list: 'banner-list',
    add: 'add-banner',
    edit: 'edit-banner',
  },
  [routeKeys.contactUsKey]: {
    slug: 'contact-us',
    list: 'contact-list',
  },
  [routeKeys.seoKey]: {
    slug: 'seo',
    list: 'seo-list',
    add: 'add-seo',
    edit: 'edit-seo',
  },
  [routeKeys.notificationKey]: {
    slug: 'notification',
    list: 'notification-list',
    statusHistory: 'status-history',
  },
  [routeKeys.categoriesKey]: {
    slug: 'categories',
    list: 'categories-list',
    add: 'add-category',
    edit: 'edit-category',
  },
  [routeKeys.pagesKey]: {
    slug: 'pages',
    list: 'pages-list',
    add: 'add-page',
    edit: 'edit-page',
  },
  [routeKeys.informationPolicyKey]: {
    slug: 'information-policy',
    list: 'information-policy-list',
    add: 'add-information-policy',
    edit: 'edit-information-policy',
  },
  [routeKeys.adminSettingsKey]: {
    slug: 'admin-settings',
    list: 'edit-admin-settings',
    add: 'add-admin-settings',
    edit: 'edit-admin-settings',
  },
  [routeKeys.configSettingsKey]: {
    slug: 'config-settings',
    list: 'edit-config-settings',
    add: 'add-config-settings',
    edit: 'edit-config-settings',
  },
  [routeKeys.taxCategoryKey]: {
    slug: 'tax-category',
    list: 'tax-category-list',
    add: 'add-tax-category',
    edit: 'edit-tax-category',
  },
  [routeKeys.holidayKey]: {
    slug: 'holiday',
    list: 'holiday-list',
  },
  [routeKeys.employerKey]: {
    slug: 'employer',
    list: 'employer-list',
    add: 'add-employer',
    edit: 'edit-employer',
    statusHistory: 'status-history',
  },
  [routeKeys.branchKey]: {
    slug: 'branch',
    list: 'branch-list',
    add: 'add-branch',
    edit: 'edit-branch',
  },
  [routeKeys.staffKey]: {
    slug: 'staff',
    list: 'staff-list',
    add: 'add-staff',
    edit: 'edit-staff',
    statusHistory: 'status-history',
    deductionHistory: 'deduction-history',
  },
  [routeKeys.shiftKey]: {
    slug: 'shift',
    list: 'shift-list',
    add: 'add-shift',
    edit: 'edit-shift',
    statusHistory: 'status-history',
  },
  [routeKeys.transactionKey]: {
    slug: 'transaction',
    list: 'transaction-list',
    add: 'add-transaction',
    edit: 'edit-transaction',
    viewDetails: 'view-details',
    statusHistory: 'status-history',
  },
  [routeKeys.ledgerKey]: {
    slug: 'ledger',
    list: 'ledger-list',
    viewDetails: 'view-details',
    statusHistory: 'status-history',
  },
  [routeKeys.shiftApplicationKey]: {
    slug: 'shift-application',
    list: 'shift-application-list',
    add: 'add-shift-application',
    statusHistory: 'status-history',
  },
  [routeKeys.timesheetKey]: {
    slug: 'timesheet',
    list: 'timesheet-list',
    add: 'add-timesheet',
    edit: 'edit-timesheet',
    statusHistory: 'status-history',
  },
  [routeKeys.staffDocumentsKey]: {
    slug: 'staff-documents',
    list: 'staff-documents-list',
    add: 'add-staff-documents',
    edit: 'edit-staff-documents',
    statusHistory: 'status-history',
  },
  [routeKeys.staffAvailabilityKey]: {
    slug: 'staff-availability',
    list: 'staff-availability-list',
    add: 'add-staff-availability',
    edit: 'edit-staff-availability',
  },
  [routeKeys.declarationChecklistKey]: {
    slug: 'declaration-checklist',
    list: 'declaration-checklist-list',
    viewDetails: 'view-details',
  },
}

export const ADMIN_ROUTE_SEGMENTS = {
  [routeKeys.subadminsKey]: {
    slug: 'subadmins',
    list: 'subadmins-list',
    add: 'add-subAdmin',
    edit: 'edit-subAdmin',
  },
  [routeKeys.subadminRolesKey]: {
    slug: 'subadmin-roles',
    list: 'subadmin-roles-list',
    add: 'add-subadmin-role',
    edit: 'edit-subadmin-role',
  },
}

export const getPageSegment = (permissionKey, segment) =>
  PAGE_ROUTE_SEGMENTS[permissionKey]?.[segment]

export const getPageBasePath = (permissionKey) => {
  const slug = getPageSegment(permissionKey, 'slug')
  return slug ? `${PAGES_PREFIX}/${slug}` : PAGES_PREFIX
}

export const getPageWildcardPath = (permissionKey) =>
  `${getPageBasePath(permissionKey)}/*`

export const getPageListPath = (permissionKey) => {
  const list = getPageSegment(permissionKey, 'list')
  return list ? `${getPageBasePath(permissionKey)}/${list}` : getPageBasePath(permissionKey)
}

export const getPageAddPath = (permissionKey) =>
  `${getPageBasePath(permissionKey)}/${getPageSegment(permissionKey, 'add')}`

export const getPageEditBasePath = (permissionKey) =>
  `${getPageBasePath(permissionKey)}/${getPageSegment(permissionKey, 'edit')}`

export const getPageEditPath = (permissionKey, id) =>
  `${getPageEditBasePath(permissionKey)}/${id}`

/** Relative route segment for nested `<Route path=... />`. */
export const getPageListRoute = (permissionKey) =>
  getPageSegment(permissionKey, 'list')

export const getPageAddRoute = (permissionKey) =>
  getPageSegment(permissionKey, 'add')

export const getPageEditRoute = (permissionKey) =>
  `${getPageSegment(permissionKey, 'edit')}/:id`

export const getPageListNavigateTo = (permissionKey, pageSize) =>
  `${getPageListRoute(permissionKey)}?page=1&limit=${pageSize}`

export const getPageListPathWithPagination = (permissionKey, pageSize) =>
  `${getPageListPath(permissionKey)}?page=1&limit=${pageSize}`

export const getPageStatusHistoryPath = (permissionKey, id) => {
  const list = getPageSegment(permissionKey, 'list')
  const statusHistory = getPageSegment(permissionKey, 'statusHistory')
  return `${getPageBasePath(permissionKey)}/${list}/${statusHistory}/${id}`
}

export const getPageStatusHistoryRoute = (permissionKey) => {
  const list = getPageSegment(permissionKey, 'list')
  const statusHistory = getPageSegment(permissionKey, 'statusHistory')
  return `${list}/${statusHistory}/:notificationId`
}

export const getPageViewDetailsPath = (permissionKey, id) => {
  const list = getPageSegment(permissionKey, 'list')
  const viewDetails = getPageSegment(permissionKey, 'viewDetails')
  return `${getPageBasePath(permissionKey)}/${list}/${viewDetails}/${id}`
}

export const getPageViewDetailsRoute = (permissionKey) => {
  const list = getPageSegment(permissionKey, 'list')
  const viewDetails = getPageSegment(permissionKey, 'viewDetails')
  return `${list}/${viewDetails}/:id`
}

/** Typed view path for Declarations & Checklists (type + staffId). */
export const getDeclarationChecklistViewPath = (type, staffId) => {
  const list = getPageSegment(routeKeys.declarationChecklistKey, 'list')
  const viewDetails = getPageSegment(
    routeKeys.declarationChecklistKey,
    'viewDetails'
  )
  return `${getPageBasePath(routeKeys.declarationChecklistKey)}/${list}/${viewDetails}/${encodeURIComponent(type)}/${staffId}`
}

export const getDeclarationChecklistViewRoute = () => {
  const list = getPageSegment(routeKeys.declarationChecklistKey, 'list')
  const viewDetails = getPageSegment(
    routeKeys.declarationChecklistKey,
    'viewDetails'
  )
  return `${list}/${viewDetails}/:type/:staffId`
}

export const getPageDeductionHistoryPath = (permissionKey, id) => {
  const list = getPageSegment(permissionKey, 'list')
  const deductionHistory = getPageSegment(permissionKey, 'deductionHistory')
  return `${getPageBasePath(permissionKey)}/${list}/${deductionHistory}/${id}`
}

export const getPageDeductionHistoryRoute = (permissionKey) => {
  const list = getPageSegment(permissionKey, 'list')
  const deductionHistory = getPageSegment(permissionKey, 'deductionHistory')
  return `${list}/${deductionHistory}/:notificationId`
}

export const getAdminSegment = (permissionKey, segment) =>
  ADMIN_ROUTE_SEGMENTS[permissionKey]?.[segment]

export const getAdminBasePath = (permissionKey) => {
  const slug = getAdminSegment(permissionKey, 'slug')
  return slug ? `${ADMINS_PREFIX}/${slug}` : ADMINS_PREFIX
}

export const getAdminWildcardPath = (permissionKey) =>
  `${getAdminBasePath(permissionKey)}/*`

export const getAdminListPath = (permissionKey) =>
  `${getAdminBasePath(permissionKey)}/${getAdminSegment(permissionKey, 'list')}`

export const getAdminAddPath = (permissionKey) =>
  `${getAdminBasePath(permissionKey)}/${getAdminSegment(permissionKey, 'add')}`

export const getAdminEditBasePath = (permissionKey) =>
  `${getAdminBasePath(permissionKey)}/${getAdminSegment(permissionKey, 'edit')}`

export const getAdminEditPath = (permissionKey, id) =>
  `${getAdminEditBasePath(permissionKey)}/${id}`

export const getAdminListRoute = (permissionKey) =>
  getAdminSegment(permissionKey, 'list')

export const getAdminAddRoute = (permissionKey) =>
  getAdminSegment(permissionKey, 'add')

export const getAdminEditRoute = (permissionKey) =>
  `${getAdminSegment(permissionKey, 'edit')}/:id`

export const getAdminListNavigateTo = (permissionKey, pageSize) =>
  `${getAdminListRoute(permissionKey)}?page=1&limit=${pageSize}`
